import Testimonial from "@/components/ui/testimonial";
import { cn, withUrl } from "@/lib/utils";
import Link from "next/link";
import {
  TestimonialData,
  ServiceData,
  SERVICE_DATA,
  TESTIMONIAL_DATA,
} from "@/lib/data";
import PrimaryButton from "@/components/ui/primary-button";
import Image from "next/image";

import ALCA from "@/assets/images/home/awards/alca.png";
import BestOfHomeCare from "@/assets/images/home/awards/best-of-home-care.png";
import HCAO from "@/assets/images/home/awards/hcao.png";
import HomeCarePulse from "@/assets/images/home/awards/home-care-pulse.png";
import NACCM from "@/assets/images/home/awards/naccm.png";
import NAHCH from "@/assets/images/home/awards/nahch.png";
import TorchAwards from "@/assets/images/home/awards/torch-awards.png";
import WBENC from "@/assets/images/home/awards/wbenc.png";
import LINKS from "@/lib/links";

type Cta = {
  href: string;
  text: string;
};

export interface TestimonialsProps {
  id?: string;
  className?: string;
  heading?: string;
  subheading?: string;
  data?: ServiceData[];
  testimonials?: TestimonialData[];
  primaryCta?: Cta;
}

const awardImages = [
  { src: WBENC, alt: "Certified WBENC - Women's Business Enterprise" },
  { src: NACCM, alt: "National Academy of Certified Care Managers" },
  { src: ALCA, alt: "Aging Life Care Association - Corporate Partner" },
  { src: HomeCarePulse, alt: "Home Care Pulse - Trusted Provider" },
  { src: TorchAwards, alt: "Torch Awards for Ethics - 2021 Winner" },
  {
    src: NAHCH,
    alt: "National Association for Home Care & Hospice - Member 2012",
  },
  { src: BestOfHomeCare, alt: "Best of Home Care - Employer of Choice 2024" },
  {
    src: HCAO,
    alt: "Certified Home Care Aide Organization - California Association for Health Services at Home",
  },
];

const defaultProps: TestimonialsProps = {
  heading: "Trusted by Many, Loved by All",
  subheading: "We have provided care for more than 13 years in the Bay Area.",
  data: SERVICE_DATA,
  testimonials: TESTIMONIAL_DATA,
  primaryCta: {
    href: withUrl(LINKS.testimonial.href),
    text: "View All Testimonials",
  },
};

const Testimonials: React.FC<TestimonialsProps> = ({
  id = "testimonials",
  className,
  heading,
  subheading,
  data,
  testimonials,
  primaryCta,
}) => {
  // Set default props if they are not defined
  heading = heading ?? defaultProps.heading;
  subheading = subheading ?? defaultProps.subheading;
  data = data ?? defaultProps.data;
  testimonials = testimonials ?? defaultProps.testimonials;
  primaryCta = primaryCta ?? defaultProps.primaryCta;

  return (
    <section
      id={id}
      aria-label={id}
      className="bg-primary-100"
    >
      <div
        className={cn(
          "mx-auto grid w-full max-w-lg flex-col gap-4 bg-primary-100 p-4 md:gap-6 md:px-6 md:py-8 lg:grid-cols-[minmax(min-content,max-content)] lg:grid-rows-[minmax(min-content,max-content)_minmax(min-content,max-content)_minmax(min-content,max-content)] lg:gap-10 lg:gap-x-[119px] lg:px-3 lg:py-12",
          className
        )}
      >
        <div className="flex flex-col gap-4 text-center md:gap-6 lg:gap-8 lg:text-left">
          <div className="flex flex-col gap-2 md:gap-3 lg:gap-4">
            <h2 className="font-montserrat text-heading-4 text-secondary-500 md:text-heading-3 lg:text-heading-2">
              {heading}
            </h2>
            <p className="font-montserrat text-subheading-3-400 text-neutral-500 md:whitespace-nowrap lg:text-subheading-2">
              {subheading}
            </p>
          </div>
          {primaryCta && (
            <PrimaryButton
              id="primary-cta-home-testimonials"
              className="mx-auto w-fit lg:mx-0"
              color="teal"
              size="md"
              asChild
            >
              <Link
                href={primaryCta.href}
                prefetch
              >
                {primaryCta.text}
              </Link>
            </PrimaryButton>
          )}
        </div>
        <div className="flex flex-row justify-between md:justify-around lg:justify-between">
          {data?.map((datum, index) => (
            <Data
              key={`${datum.label}-${index}`}
              count={datum.count}
              label={datum.label}
            />
          ))}
        </div>
        <div
          className={cn(
            "flex w-full justify-around md:gap-4 lg:col-start-2 lg:row-span-full lg:flex-col lg:justify-between",
            "[&>*:nth-child(1)]:flex [&>*:nth-child(2)]:hidden [&>*:nth-child(3)]:hidden",
            "md:[&>*:nth-child(1)]:flex md:[&>*:nth-child(2)]:flex md:[&>*:nth-child(3)]:hidden",
            "lg:[&>*:nth-child(1)]:flex lg:[&>*:nth-child(2)]:flex lg:[&>*:nth-child(3)]:flex"
          )}
        >
          {testimonials?.map((testimonial, index) => (
            <Testimonial
              key={`${testimonial.name}-${index}`}
              className={
                "bg-white md:h-[204px] md:w-[340px] lg:h-[180px] lg:w-[405px]"
              }
              variant="stars"
              stars={testimonial.stars}
              name={testimonial.name}
              date={testimonial.date}
              imageURL={testimonial.imageURL}
            >
              {testimonial?.text}
            </Testimonial>
          ))}
        </div>
        <div className="grid grid-cols-4 gap-y-2 md:gap-y-4">
          {awardImages.map(({ src, alt }) => (
            <Image
              src={src}
              alt={alt}
              key={"award-image-" + alt}
              className="mx-auto max-w-full object-contain p-1 lg:p-2"
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const Data: React.FC<ServiceData> = ({ count, label }) => {
  return (
    <div className="py-2 text-center">
      <p className="font-montserrat text-heading-4 text-primary-500 md:text-heading-2">
        {count.toLocaleString()}+
      </p>
      <p className="font-montserrat text-body-2-300 text-neutral-500 md:text-body-1-300">
        {label}
      </p>
    </div>
  );
};

export default Testimonials;
